import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../providers/supabase/supabase';
import { ArtistMetrics } from '../../hooks/useArtistMetrics';
import { fetchCmTrackCount } from '../utils/chartmetricApi';
import { HistoricalDataPoint } from '../utils/dealCalculations';
import { EventData } from '../utils/revenueProjections';

// Safe number conversion (reused from useArtistMetrics pattern)
const safeParseNumber = (value: string | null | undefined): number | null => {
    if (!value || value === '' || value === 'null' || value === 'undefined') return null;
    const num = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(num) ? null : num;
};

export interface ArtistDealData {
    artistName: string;
    artistId: string;
    metrics: ArtistMetrics | null;
    history: HistoricalDataPoint[];
    events: EventData[];
    editorialPlaylistCount: number;
    catalogSize: number;
    loading: boolean;
    error: string | null;
    debugInfo: string;
}

export function useArtistDealData(artistId: string | null): ArtistDealData & { refetch: () => void } {
    const [data, setData] = useState<ArtistDealData>({
        artistName: '',
        artistId: '',
        metrics: null,
        history: [],
        events: [],
        editorialPlaylistCount: 0,
        catalogSize: 1,
        loading: false,
        error: null,
        debugInfo: '',
    });

    const fetchData = useCallback(async () => {
        if (!artistId) {
            setData((prev) => ({ ...prev, metrics: null, history: [], events: [], loading: false, error: null }));
            return;
        }

        setData((prev) => ({ ...prev, loading: true, error: null }));

        try {
            // Parallel fetch: latest FULL snapshot, full history, events, artist name
            // For metrics card, we need a row with real data (not a CM backfill row
            // that only has sp_monthly_listeners). Filter for rows where cm_artist_rank
            // is present — those come from the discovery script with full metrics.
            const [latestFullResult, latestAnyResult, historyResult, eventsResult, artistResult, trackCountResult] = await Promise.all([
                supabase
                    .from('artists_history')
                    .select('*')
                    .eq('artist_id', artistId)
                    .not('cm_artist_rank', 'is', null)
                    .order('snapshot_date', { ascending: false })
                    .limit(1)
                    .single(),

                // Fallback: if no full snapshot exists, grab whatever is latest
                supabase
                    .from('artists_history')
                    .select('*')
                    .eq('artist_id', artistId)
                    .order('snapshot_date', { ascending: false })
                    .limit(1)
                    .single(),

                supabase
                    .from('artists_history')
                    .select('snapshot_date, sp_monthly_listeners')
                    .eq('artist_id', artistId)
                    .order('snapshot_date', { ascending: true }),

                supabase
                    .from('events')
                    .select('venue_capacity, ticket_price_low, ticket_price_high')
                    .eq('artist_id', artistId),

                supabase
                    .from('artists')
                    .select('name, spotify_url, instagram_url, facebook_url, website_url')
                    .eq('id', artistId)
                    .single(),

                supabase
                    .from('tracks')
                    .select('*', { count: 'exact', head: true })
                    .eq('artist_id', artistId),
            ]);

            // Transform latest snapshot into ArtistMetrics
            // Prefer the full snapshot (has cm_artist_rank), fall back to any latest row
            let metrics: ArtistMetrics | null = null;
            const latestData = latestFullResult.data ?? latestAnyResult.data;
            if (latestData) {
                const raw = latestData;
                const spotify_followers = safeParseNumber(raw.sp_followers);
                const instagram_followers = safeParseNumber(raw.ins_followers);
                const youtube_subscribers = safeParseNumber(raw.ycs_subscribers);
                const tiktok_followers = safeParseNumber(raw.tiktok_followers);
                const facebook_followers = safeParseNumber(raw.facebook_followers);
                const twitter_followers = safeParseNumber(raw.ts_followers);

                const total_social_followers = [
                    spotify_followers, instagram_followers, youtube_subscribers,
                    tiktok_followers, facebook_followers, twitter_followers,
                ].reduce<number>((sum, val) => sum + (val || 0), 0);

                const monthlyListeners = safeParseNumber(raw.sp_monthly_listeners) || 0;
                const artistRank = safeParseNumber(raw.cm_artist_rank) || 999999;
                let career_stage = 'New';
                if (monthlyListeners > 1000000 || artistRank < 1000) career_stage = 'Mainstream';
                else if (monthlyListeners > 100000 || artistRank < 10000) career_stage = 'Developing';
                else if (monthlyListeners > 10000 || artistRank < 50000) career_stage = 'Emerging';

                metrics = {
                    artist_id: raw.artist_id,
                    snapshot_date: new Date(raw.snapshot_date),
                    cm_artist_score: safeParseNumber(raw.cm_artist_score),
                    cm_artist_rank: safeParseNumber(raw.cm_artist_rank),
                    spotify_followers,
                    spotify_monthly_listeners: safeParseNumber(raw.sp_monthly_listeners),
                    spotify_popularity: safeParseNumber(raw.sp_popularity),
                    spotify_playlist_reach: safeParseNumber(raw.sp_playlist_total_reach),
                    spotify_playlist_count: safeParseNumber(raw.spotify_playlist_count),
                    instagram_followers,
                    instagram_engagement_rate: safeParseNumber(raw.ins_engagement_rate),
                    youtube_subscribers,
                    youtube_views: safeParseNumber(raw.ycs_views),
                    youtube_monthly_views: safeParseNumber(raw.youtube_monthly_video_views),
                    youtube_engagement_rate: safeParseNumber(raw.youtube_engagement_rate),
                    tiktok_followers,
                    tiktok_likes: safeParseNumber(raw.tiktok_likes),
                    tiktok_engagement_rate: safeParseNumber(raw.tiktok_engagement_rate),
                    facebook_followers,
                    facebook_likes: safeParseNumber(raw.facebook_likes),
                    twitter_followers,
                    soundcloud_followers: safeParseNumber(raw.soundcloud_followers),
                    soundcloud_plays: safeParseNumber(raw.soundcloud_plays),
                    deezer_fans: safeParseNumber(raw.deezer_fans),
                    shazam_count: safeParseNumber(raw.shazam_count),
                    pandora_listeners: safeParseNumber(raw.pandora_listeners_28_day),
                    genius_views: safeParseNumber(raw.genius_views),
                    wikipedia_views: safeParseNumber(raw.wikipedia_views),
                    bandsintown_followers: safeParseNumber(raw.bandsintown_followers),
                    songkick_fans: safeParseNumber(raw.songkick_fans),
                    total_social_followers,
                    growth_trend: null,
                    career_stage,
                };
            }

            // Build historical data points from snapshots, deduplicated by date
            const historyMap = new Map<string, HistoricalDataPoint>();
            for (const row of historyResult.data || []) {
                const dateKey = row.snapshot_date?.slice(0, 10); // YYYY-MM-DD
                const listeners = safeParseNumber(row.sp_monthly_listeners) || 0;
                if (!dateKey || listeners <= 0) continue;
                // Keep latest value per date (later rows overwrite earlier ones)
                historyMap.set(dateKey, {
                    date: new Date(dateKey),
                    monthlyListeners: listeners,
                });
            }
            let history = Array.from(historyMap.values())
                .sort((a, b) => a.date.getTime() - b.date.getTime());

            // Chartmetric enrichment
            let cmTrackCount = 0;
            const localSpanDays = history.length >= 2
                ? (history[history.length - 1].date.getTime() - history[0].date.getTime()) / (1000 * 60 * 60 * 24)
                : 0;

            try {
                // Always fetch track count (fast)
                cmTrackCount = await fetchCmTrackCount(artistId);

                // If local history is sparse, trigger server-side backfill to Supabase
                if (localSpanDays < 60) {
                    const backfillResp = await fetch(`/api/cm-backfill?id=${artistId}`, { method: 'POST' });
                    const backfillResult = await backfillResp.json();

                    if (backfillResult.status === 'backfilled' && backfillResult.inserted > 0) {
                        // Re-fetch history from Supabase now that it has CM data
                        const { data: refreshedHistory } = await supabase
                            .from('artists_history')
                            .select('snapshot_date, sp_monthly_listeners')
                            .eq('artist_id', artistId)
                            .order('snapshot_date', { ascending: true });

                        const refreshedMap = new Map<string, HistoricalDataPoint>();
                        for (const row of refreshedHistory || []) {
                            const dateKey = row.snapshot_date?.slice(0, 10);
                            const listeners = safeParseNumber(row.sp_monthly_listeners) || 0;
                            if (!dateKey || listeners <= 0) continue;
                            refreshedMap.set(dateKey, {
                                date: new Date(dateKey),
                                monthlyListeners: listeners,
                            });
                        }
                        history = Array.from(refreshedMap.values())
                            .sort((a, b) => a.date.getTime() - b.date.getTime());
                    }
                }
            } catch (cmError) {
                console.warn('Chartmetric enrichment failed:', cmError);
            }

            // Calculate growth trend from history
            if (metrics && history.length >= 2) {
                const first = history[0].monthlyListeners;
                const last = history[history.length - 1].monthlyListeners;
                if (first > 0) {
                    const growth = (last - first) / first;
                    if (growth > 0.05) metrics.growth_trend = 'up';
                    else if (growth < -0.05) metrics.growth_trend = 'down';
                    else metrics.growth_trend = 'stable';
                }
            }

            // Events
            const events: EventData[] = (eventsResult.data || []).map((row: any) => ({
                venue_capacity: safeParseNumber(row.venue_capacity),
                ticket_price_low: safeParseNumber(row.ticket_price_low),
                ticket_price_high: safeParseNumber(row.ticket_price_high),
            }));

            // Artist name + catalog size (prefer Chartmetric, fall back to DB)
            const artistName = artistResult.data?.name || '';
            const dbTrackCount = trackCountResult.count ?? 0;
            const catalogSize = Math.max(cmTrackCount || dbTrackCount, 1);

            setData({
                artistName,
                artistId,
                metrics,
                history,
                events,
                editorialPlaylistCount: metrics?.spotify_playlist_count ?? 0,
                catalogSize,
                loading: false,
                error: null,
                debugInfo: '',
            });
        } catch (err) {
            console.error('Error fetching deal data:', err);
            setData((prev) => ({
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : 'Failed to fetch artist data',
            }));
        }
    }, [artistId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { ...data, refetch: fetchData };
}
